import { HStack, Input, Spacer, Stack, Tag, VisuallyHidden } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import type { Dispatch, FC, SetStateAction } from 'react'

import { Button } from '@app/components/ui/button'
import { Field } from '@app/components/ui/field'
import { NativeSelectField, NativeSelectRoot } from '@app/components/ui/native-select'

interface Props {
  headers: Record<string, string>
  setHeaders: Dispatch<SetStateAction<object>>
  usedIndexes: number[]
  setUsedIndexes: Dispatch<SetStateAction<number[]>>
}

const PreviewSegments: FC<Props> = ({ headers, setHeaders, usedIndexes, setUsedIndexes }) => {
  const [segments, setSegments] = useState([])
  const [segmentName, setSegmentName] = useState('')
  const [segmentValueColumn, setSegmentValueColumn] = useState('')

  useEffect(() => {
    if (Object.keys(headers).length === 0 && segments.length > 0) {
      setSegments([])
    }
  }, [headers, segments.length])

  const addSegment = () => {
    setSegments((prev) => [...prev, { name: segmentName, valueColumn: segmentValueColumn }])
    setHeaders({ ...headers, [segmentValueColumn]: segmentName })
    setUsedIndexes([...usedIndexes, parseInt(segmentValueColumn)])

    setSegmentName('')
    setSegmentValueColumn('')
  }

  const deleteSegment = (segmentValue) => {
    setSegments((prev) => prev.filter((segment) => segment.valueColumn !== segmentValue))
    setUsedIndexes(usedIndexes.filter((index) => index !== parseInt(segmentValue)))

    const headerIndex = Object.keys(headers).findIndex((key) => key === segmentValue)

    if (headerIndex > -1) {
      setHeaders({ ...headers, [headerIndex]: `col${headerIndex}` })
    }
  }

  return (
    <Stack gap={4}>
      <HStack>
        <Field
          label="Segment name"
          required={!!segmentValueColumn}
          invalid={!!segmentValueColumn && segmentName === ''}
        >
          <Input
            placeholder="Segment name"
            value={segmentName}
            onChange={(e) => setSegmentName(e.currentTarget.value)}
          />
        </Field>
        <Field
          label="Segment value column"
          required={!!segmentName}
          invalid={!!segmentName && segmentValueColumn === ''}
        >
          <NativeSelectRoot>
            <NativeSelectField
              value={segmentValueColumn}
              onChange={(e) => setSegmentValueColumn(e.currentTarget.value)}
            >
              <option value="">Pick a column</option>
              {Object.keys(headers).map((key) => (
                <option key={key} value={key} disabled={usedIndexes.includes(parseInt(key))}>
                  {headers[key]}
                </option>
              ))}
            </NativeSelectField>
          </NativeSelectRoot>
        </Field>
        <Spacer />
        <Field label="&nbsp;">
          <Button onClick={addSegment} disabled={!segmentName || !segmentValueColumn}>
            Add
          </Button>
        </Field>
      </HStack>
      <VisuallyHidden>
        <Input
          name="segments"
          value={JSON.stringify(
            segments.map((segment) => ({ name: segment.name, valueIndex: Number(segment.valueColumn) }))
          )}
          readOnly
        />
      </VisuallyHidden>
      <HStack wrap="wrap">
        {segments.map((segment) => (
          <Tag.Root key={segment.valueColumn} size="lg" variant="subtle" colorPalette="green">
            <Tag.Label>{segment.name}</Tag.Label>
            <Tag.EndElement>
              <Tag.CloseTrigger onClick={() => deleteSegment(segment.valueColumn)} />
            </Tag.EndElement>
          </Tag.Root>
        ))}
      </HStack>
    </Stack>
  )
}

export default PreviewSegments
